import browserDetect from 'browser-detect';
import logger from '~/services/logger';

const defaultVideoType = 'application/x-mpegURL';
const WEB_PLATFORM = 'web';

export const getVideoType = (src?: string) => {
  if (src) {
    try {
      const parsedURL = new URL(src);
      const pathname = parsedURL?.pathname;

      const types: Record<string, string> = {
        aac: 'audio/aac',
        gif: 'image/gif',
        jpeg: 'image/jpeg',
        jpg: 'image/jpeg',
        m3u8: 'application/x-mpegURL',
        m4a: 'audio/mp4',
        m4v: 'video/mp4',
        mkv: 'video/x-matroska',
        mov: 'video/mp4',
        mp3: 'audio/mpeg',
        mp4: 'video/mp4',
        mpd: 'application/dash+xml',
        oga: 'audio/ogg',
        ogv: 'video/ogg',
        opus: 'video/ogg',
        png: 'image/png',
        svg: 'image/svg+xml',
        webp: 'image/webp',
      };

      const extension =
        (pathname || '.m3u8').split('.').pop()?.toLowerCase() || 'm3u8';

      const type = types[extension] || defaultVideoType;

      return type;
    } catch (error) {
      logger.info(`failed to get video type: ${error}`);

      return defaultVideoType;
    }
  }

  return defaultVideoType;
};

function encodeVmapParams(
  params: Record<string, string>
): Record<string, string> {
  return Object.fromEntries(
    Object.entries(params).map(([entryKey, value]) => [
      entryKey,
      encodeURIComponent(value),
    ])
  );
}

function getBrowserOsVersion(): string {
  if (typeof navigator === 'undefined') {
    return '';
  }

  const userAgent = navigator.userAgent;
  const osVersionMatch =
    userAgent.match(/(?:Windows NT|Mac OS X|Android|CrOS)\s*([0-9\._]+)/i) ??
    userAgent.match(/(?:iPhone|iPad|iPod).*?OS\s*([0-9_]+)/i);

  return osVersionMatch?.[1]?.replace(/_/g, '.') ?? '';
}

function getBrowserName(userAgent: string): string | undefined {
  const browser = browserDetect(userAgent || '');

  if (browser?.mobile && browser?.os === 'OS X') {
    return 'safari';
  }

  return browser?.name;
}

function getBrowserInfo(): { mfgr: string; model: string } | null {
  if (typeof navigator === 'undefined') {
    return null;
  }

  const userAgent = navigator.userAgent || '';
  const mfgr = getBrowserName(userAgent);
  const browser = browserDetect(userAgent);
  const model = browser?.version;

  if (!mfgr || !model) {
    return null;
  }

  return { mfgr, model };
}

/**
 * Builds SSAI VMAP params for zapp-client browser web playback.
 *
 * @returns Encoded VMAP params, or `undefined` when required browser fields are missing.
 */
export function buildSsaiVmapUrlParamsForWeb():
  | Record<string, string>
  | undefined {
  try {
    const browserInfo = getBrowserInfo();
    const os = getBrowserOsVersion();

    if (!browserInfo || !os) {
      return undefined;
    }

    const params: Record<string, string> = {
      platform: WEB_PLATFORM,
      os,
      model: browserInfo.model,
      mfgr: browserInfo.mfgr,
    };

    if (!params.model || !params.mfgr) {
      return undefined;
    }

    return encodeVmapParams(params);
  } catch (error) {
    logger.warn(
      `SSAI ad targeting: failed to build browser vmapURLParams - ${error}`
    );

    return undefined;
  }
}
